import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';
function isDivider(entry) {
  return 'type' in entry && entry.type === 'divider';
}
export function Dropdown({ trigger, items, align = 'right', position = 'bottom', className }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const activeIndex = useRef(-1);
  const actionItems = items.filter((item) => !isDivider(item));
  const close = useCallback(() => {
    setOpen(false);
    setMenuPos(null);
    activeIndex.current = -1;
  }, []);

  function calcPosition() {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    if (position === 'top') {
      return {
        bottom: window.innerHeight - rect.top + 4,
        left: align === 'right' ? rect.right + window.scrollX : rect.left + window.scrollX,
      };
    }
    return {
      top: rect.bottom + window.scrollY + 4,
      left: align === 'right' ? rect.right + window.scrollX : rect.left + window.scrollX,
    };
  }

  function openMenu() {
    setMenuPos(calcPosition());
    setOpen(true);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, close]);

  // Close on scroll (parent containers)
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => close();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open, close]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu();
        activeIndex.current = 0;
        return;
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        activeIndex.current = Math.min(activeIndex.current + 1, actionItems.length - 1);
        focusActiveItem();
        break;
      case 'ArrowUp':
        e.preventDefault();
        activeIndex.current = Math.max(activeIndex.current - 1, 0);
        focusActiveItem();
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const item = actionItems[activeIndex.current];
        if (item && !item.disabled) {
          item.onClick();
          close();
        }
        break;
      }
    }
  };
  const focusActiveItem = () => {
    if (menuRef.current) {
      const buttons = menuRef.current.querySelectorAll('[role="menuitem"]');
      buttons[activeIndex.current]?.focus();
    }
  };

  // Focus first item when opening
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => focusActiveItem());
    }
  }, [open]);

  const menu = open && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          className={cn(
            'fixed z-50 min-w-[12rem] rounded-md border border-edge-strong bg-surface py-1 shadow-lg',
            'animate-in fade-in zoom-in-95 duration-150',
          )}
          style={{
            top: menuPos.top ?? undefined,
            bottom: menuPos.bottom ?? undefined,
            left: align === 'right' ? undefined : menuPos.left,
            right: align === 'right' ? document.documentElement.clientWidth - menuPos.left : undefined,
          }}
          onKeyDown={handleKeyDown}
        >
          {items.map((entry, i) => {
            if (isDivider(entry)) {
              return (
                <div
                  key={`divider-${i}`}
                  className="my-1 border-t border-edge-strong"
                  role="separator"
                />
              );
            }
            return (
              <button
                key={i}
                role="menuitem"
                type="button"
                disabled={entry.disabled}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                  'focus:bg-surface-muted focus:outline-none',
                  entry.destructive
                    ? 'text-danger-600 hover:bg-danger-50'
                    : 'text-fg-secondary hover:bg-surface-secondary',
                  entry.disabled && 'cursor-not-allowed opacity-50',
                )}
                onClick={() => {
                  if (!entry.disabled) {
                    entry.onClick();
                    close();
                  }
                }}
              >
                {entry.icon && <span className="h-4 w-4 shrink-0">{entry.icon}</span>}
                {entry.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <div
        ref={triggerRef}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {trigger}
      </div>
      {menu}
    </div>
  );
}
